import React, { useState } from 'react';
import { OfficerLayout } from '../../components/layout/OfficerLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Search, Calendar, ChevronRight, AlertCircle, CheckCircle2, Clock, Share2, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Mock Assignments
const assignments = [{
  id: '1',
  title: 'Kampanye HPSN 2025: Pilah Sampah',
  deadline: '21 Feb 2025',
  status: 'pending',
  platforms: ['Instagram', 'TikTok'],
  completedPlatforms: 0,
  totalPlatforms: 2,
  isUrgent: true
}, {
  id: '2',
  title: 'Video Edukasi Maggot BSF',
  deadline: '25 Feb 2025',
  status: 'submitted',
  platforms: ['YouTube'],
  completedPlatforms: 1,
  totalPlatforms: 1,
  isUrgent: false
}, {
  id: '3',
  title: 'Infografis Bank Sampah Unit',
  deadline: '10 Feb 2025',
  status: 'validated',
  platforms: ['Instagram', 'Facebook'],
  completedPlatforms: 2,
  totalPlatforms: 2,
  isUrgent: false
}];
export function DistribusiDariPusatPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, completed
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <StatusBadge status="kuning" data-id="element-2852">Belum Selesai</StatusBadge>;
      case 'submitted':
        return <StatusBadge status="biru" data-id="element-2853">Menunggu Validasi</StatusBadge>;
      case 'validated':
        return <StatusBadge status="hijau" data-id="element-2854">Selesai</StatusBadge>;
      default:
        return null;
    }
  };
  const filteredAssignments = assignments.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase());
    if (filter === 'all') return matchesSearch;
    if (filter === 'pending') return matchesSearch && item.status === 'pending';
    if (filter === 'completed') return matchesSearch && item.status === 'validated';
    return matchesSearch;
  });
  return <OfficerLayout title="Tugas Distribusi Pusat" data-id="element-2855">
      <div className="space-y-4" data-id="element-2856">
        {/* Search & Filter */}
        <div className="flex gap-2" data-id="element-2857">
          <div className="relative flex-1" data-id="element-2858">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-2859" />
            <Input placeholder="Cari tugas..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2860" />
          </div>
          <Button variant="outline" size="icon" data-id="element-2861">
            <Filter className="h-4 w-4" data-id="element-2862" />
          </Button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-2863">
          <div className="bg-orange-50 p-3 rounded-lg border border-orange-100 text-center cursor-pointer hover:bg-orange-100" onClick={() => setFilter('pending')} data-id="element-2864">
            <div className="text-xl font-bold text-orange-600" data-id="element-2865">1</div>
            <div className="text-[10px] text-orange-800" data-id="element-2866">Pending</div>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-center cursor-pointer hover:bg-blue-100" data-id="element-2867">
            <div className="text-xl font-bold text-blue-600" data-id="element-2868">1</div>
            <div className="text-[10px] text-blue-800" data-id="element-2869">Review</div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg border border-green-100 text-center cursor-pointer hover:bg-green-100" onClick={() => setFilter('completed')} data-id="element-2870">
            <div className="text-xl font-bold text-green-600" data-id="element-2871">12</div>
            <div className="text-[10px] text-green-800" data-id="element-2872">Selesai</div>
          </div>
        </div>

        {/* List */}
        <div className="space-y-3 pb-20" data-id="element-2873">
          {filteredAssignments.map(item => <Card key={item.id} className="active:scale-[0.99] transition-transform cursor-pointer hover:shadow-md" onClick={() => navigate(`/officer/distribusi/${item.id}`)} data-id="element-2874">
              <CardContent className="p-4" data-id="element-2875">
                <div className="flex justify-between items-start mb-2" data-id="element-2876">
                  {getStatusBadge(item.status)}
                  {item.isUrgent && item.status === 'pending' && <Badge variant="destructive" className="animate-pulse" data-id="element-2877">
                      Segera
                    </Badge>}
                </div>

                <h3 className="font-bold text-gray-900 mb-2" data-id="element-2878">{item.title}</h3>

                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3" data-id="element-2879">
                  <div className="flex items-center gap-1" data-id="element-2880">
                    <Calendar className="h-3 w-3" data-id="element-2881" /> Deadline: {item.deadline}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-3" data-id="element-2882">
                  <div className="flex justify-between text-[10px] text-gray-500 mb-1" data-id="element-2883">
                    <span data-id="element-2884">Progress Platform</span>
                    <span data-id="element-2885">
                      {item.completedPlatforms}/{item.totalPlatforms} Selesai
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-2886">
                    <div className={`h-1.5 rounded-full ${item.completedPlatforms === item.totalPlatforms ? 'bg-green-500' : 'bg-blue-500'}`} style={{
                  width: `${item.completedPlatforms / item.totalPlatforms * 100}%`
                }} data-id="element-2887"></div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-3 border-t" data-id="element-2888">
                  <div className="flex gap-1" data-id="element-2889">
                    {item.platforms.map(p => <span key={p} className="text-[10px] px-2 py-0.5 bg-gray-100 rounded text-gray-600 border" data-id="element-2890">
                        {p}
                      </span>)}
                  </div>
                  <Button size="sm" variant="ghost" className="h-8 text-primary-600 p-0 hover:bg-transparent" data-id="element-2891">
                    Buka <ChevronRight className="h-4 w-4 ml-1" data-id="element-2892" />
                  </Button>
                </div>
              </CardContent>
            </Card>)}

          {filteredAssignments.length === 0 && <div className="text-center py-10 text-gray-500" data-id="element-2893">
              <p data-id="element-2894">Tidak ada tugas ditemukan</p>
            </div>}
        </div>
      </div>
    </OfficerLayout>;
}